import {_initVuexPropValFun} from "@/main-page/ctls/hooks/useVuexState";
import {usePageModelsStore} from "@/store/modules/pageModels";
import {storeToRefs} from "pinia";
import {StorageUtils} from "xmgoing-utils";

/** 只有布尔 true 或字符串 "true" 算文本直写 */
export function isTextDirectValue(value: unknown) {
  return value === true || value === 'true';
}

/** 支持「文本直写」的列类型：关联表单单表编辑、销售/库存/采购物料编辑 */
const TEXT_DIRECT_COLUMN_TYPES = [
  'xmRelFormEditor',
  'MaterialsSelEditor',
  'MaterialsWaEditor',
  'MaterialsPurchaseEditor',
];

export function isXmRelFormEditorColumn(column: any) {
  const type = column?.controlType || column?.propType;
  return type === 'xmRelFormEditor';
}

export function supportsTextDirectColumn(column: any) {
  const type = column?.controlType || column?.propType;
  return TEXT_DIRECT_COLUMN_TYPES.indexOf(type) !== -1;
}

/** 文本直写开启后，这一列改走普通文本列的格子 */
export function textInputShowType(editorOn: boolean, clickOn: boolean) {
  if (editorOn) {
    return 'xmInputEditor';
  }
  if (clickOn) {
    return 'xmInputCellClickEditor';
  }
  return 'xmInputCell';
}

function collectPaths(vuexStr: string, prefix: 'state' | 'record') {
  const paths: string[] = [];
  const regex = new RegExp(`vuex\\.${prefix}\\.([\\w]+(?:\\.[\\w]+)*)`, 'g');
  let match: RegExpExecArray | null;
  while ((match = regex.exec(vuexStr))) {
    if (!paths.includes(match[1])) {
      paths.push(match[1]);
    }
  }
  return paths;
}

function readPath(source: any, path: string) {
  let val = unref(source);
  for (const part of path.split('.')) {
    if (val == null) {
      return val;
    }
    val = val[part];
  }
  return val;
}

/** 点到表达式用到的字段，让计算属性只跟着这些字段变 */
function touchPaths(source: any, paths: string[], broad: boolean) {
  if (paths.length > 0) {
    paths.forEach((path) => {
      void readPath(source, path);
    });
    return;
  }
  if (!broad) {
    return;
  }
  const raw = unref(source);
  if (raw && typeof raw === 'object') {
    Object.keys(raw).forEach((key) => {
      void raw[key];
    });
  }
}

let sharedRoute: any = null;
let sharedRouteTried = false;
let sharedState: any = null;
let mesProConfigCache: any = null;

function getSharedRoute() {
  if (sharedRouteTried) {
    return sharedRoute;
  }
  sharedRouteTried = true;
  try {
    sharedRoute = useRoute();
  } catch (e) {
    sharedRoute = null;
  }
  return sharedRoute;
}

function getSharedState() {
  if (!sharedState) {
    const {state} = storeToRefs(usePageModelsStore());
    sharedState = state;
  }
  return sharedState;
}

function getMesProConfig() {
  if (mesProConfigCache) {
    return mesProConfigCache;
  }
  try {
    mesProConfigCache = JSON.parse(StorageUtils.getSessionItem("mesProConfig") || '{}');
  } catch (e) {
    mesProConfigCache = {};
  }
  return mesProConfigCache;
}

function evalTextDirectExpr(vuexStr: string, record) {
  const state = vuexStr.includes('vuex.state') ? getSharedState() : null;
  const route = vuexStr.includes('urlParams') ? getSharedRoute() : null;
  if (state) {
    touchPaths(state, collectPaths(vuexStr, 'state'), true);
  }
  const rec = unref(record);
  if (vuexStr.includes('vuex.record')) {
    touchPaths(rec, collectPaths(vuexStr, 'record'), true);
  }
  if (route) {
    void route.query;
    void route.fullPath;
  }
  const holder = {textDirect: false};
  _initVuexPropValFun(holder, {
    route,
    vuexStr,
    propName: 'textDirect',
    record: rec,
    state,
    mesProConfig: getMesProConfig(),
    recordData: {},
  });
  return isTextDirectValue(holder.textDirect);
}

/**
 * 支持文本直写的列（关联表单单表编辑、销售/库存/采购物料编辑）是否走文本直写。
 * 没配表达式时只读列上的开关，不订阅页面状态、不读路由和 session。
 * 配了 textDirectVuex 才按表达式计算，并只跟踪表达式里用到的行字段和页面状态。
 */
export function useTextDirectOn(column, record) {
  // 整个页面只取一次路由，供表达式里的 urlParams 使用；普通列不会重复取。
  if (!sharedRouteTried) {
    getSharedRoute();
  }
  return computed(() => {
    const col = unref(column);
    if (!supportsTextDirectColumn(col)) {
      return false;
    }
    const vuexStr = col.textDirectVuex;
    if (!vuexStr || typeof vuexStr !== 'string') {
      return isTextDirectValue(col.textDirect);
    }
    return evalTextDirectExpr(vuexStr, record);
  });
}
